import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
} from '@mui/material';

// Care-management codes coordinators bill most; any other CPT code can be typed in.
export const CARE_MANAGEMENT_CODES = [
  { code: '99490', description: 'CCM, clinical staff, first 20 minutes per month' },
  { code: '99439', description: 'CCM, clinical staff, each additional 20 minutes' },
  { code: '99487', description: 'Complex CCM, first 60 minutes per month' },
  { code: '99489', description: 'Complex CCM, each additional 30 minutes' },
  { code: '99491', description: 'CCM by physician/QHP, first 30 minutes per month' },
  { code: '99453', description: 'RPM device setup and patient education' },
  { code: '99454', description: 'RPM device supply with readings, 16+ days per 30' },
  { code: '99457', description: 'RPM management, first 20 minutes per month' },
  { code: '99458', description: 'RPM management, each additional 20 minutes' },
  { code: '99484', description: 'BHI care management, 20+ minutes per month' },
  { code: '99426', description: 'PCM, clinical staff, first 30 minutes per month' },
  { code: '99427', description: 'PCM, clinical staff, each additional 30 minutes' },
];

const STATUSES = ['pending', 'submitted', 'paid', 'denied', 'appealed'];

const emptyForm = () => ({
  patient_id: '',
  service_date: new Date().toISOString().slice(0, 10),
  service_code: '',
  description: '',
  diagnosis_codes: '',
  provider: '',
  amount: '',
  status: 'pending',
});

const AddBillingRecordDialog = ({ open, onClose, onRecordAdded, patients = [], apiBase }) => {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setForm(emptyForm());
      setError('');
    }
  }, [open]);

  const set = (field) => (event) => setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const handleCodeChange = (_event, value) => {
    if (value && typeof value === 'object') {
      setForm((prev) => ({ ...prev, service_code: value.code, description: prev.description || value.description }));
    } else {
      setForm((prev) => ({ ...prev, service_code: value || '' }));
    }
  };

  const missing = !form.patient_id || !form.service_date || !form.service_code || !form.description
    || !form.provider || form.amount === '' || Number(form.amount) < 0;

  const handleSave = async () => {
    if (missing) {
      setError('Fill in patient, date, code, description, provider and a non-negative amount.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const response = await fetch(`${apiBase}/billing-records`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          patient_id: Number(form.patient_id),
          amount: Number(form.amount),
          service_code: form.service_code.trim(),
          diagnosis_codes: form.diagnosis_codes.trim(),
        }),
      });
      if (!response.ok) {
        const detail = await response.json().catch(() => ({}));
        throw new Error(typeof detail.detail === 'string' ? detail.detail : `Save failed (${response.status})`);
      }
      onRecordAdded?.();
      onClose();
    } catch (err) {
      setError(`Couldn't save the billing record: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Billing Record</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={7}>
            <TextField id="billing-patient" select fullWidth required label="Patient"
              value={form.patient_id} onChange={set('patient_id')}>
              {patients.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.name}{p.mrn ? ` (${p.mrn})` : ''}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField id="billing-date" type="date" fullWidth required label="Service date"
              InputLabelProps={{ shrink: true }} value={form.service_date} onChange={set('service_date')} />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              id="billing-code"
              freeSolo
              options={CARE_MANAGEMENT_CODES}
              getOptionLabel={(option) => (typeof option === 'string' ? option : `${option.code} — ${option.description}`)}
              inputValue={form.service_code}
              onInputChange={(_e, value, reason) => {
                if (reason === 'input' || reason === 'clear') handleCodeChange(null, value);
              }}
              onChange={handleCodeChange}
              renderInput={(params) => <TextField {...params} required label="CPT code" helperText="Pick a care-management code or type any CPT code" />}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField id="billing-description" fullWidth required label="Description"
              value={form.description} onChange={set('description')} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField id="billing-icd" fullWidth label="Diagnosis codes (ICD-10)" placeholder="E11.9, I10"
              value={form.diagnosis_codes} onChange={set('diagnosis_codes')} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField id="billing-provider" fullWidth required label="Billing provider"
              value={form.provider} onChange={set('provider')} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField id="billing-amount" type="number" fullWidth required label="Amount ($)"
              inputProps={{ min: 0, step: '0.01' }} value={form.amount} onChange={set('amount')} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField id="billing-status" select fullWidth label="Status" value={form.status} onChange={set('status')}>
              {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Add record'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddBillingRecordDialog;
